import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import Swal from 'sweetalert2';

import { AuditoriaService } from '../services/AuditoriaService';
import { IEmpresaAuditoria, IPeriodoAuditoria } from '../interfaces/IAuditoria';

import {
  AcaoConfirmarPdf,
  arquivoEmAndamento,
  IAuditFile,
  IAuditInputType,
  IContaContabilBancaria,
  IImportacaoContexto,
} from './interfaces/IAuditoriaEntradaDados';
import { AuditoriaEntradaDadosService } from './services/AuditoriaEntradaDadosService';

import AuditMessage from './components/AuditMessage';
import AuditFileUploader from './components/AuditFileUploader';
import AuditImportConfirmation from './components/AuditImportConfirmation';
import AuditFileList from './components/AuditFileList';
import AuditDeleteFileModal from './components/AuditDeleteFileModal';
import AuditReplaceFileModal from './components/AuditReplaceFileModal';
import AuditFileHistory from './components/AuditFileHistory';
import AuditReconciliationResult from './components/AuditReconciliationResult';

import './styles/entrada-dados.css';

interface LocationState {
  empresaId?: number;
  periodo?: string;
}

interface IOption<T> {
  value: T;
  label: string;
}

// Enquanto houver arquivo sendo lido, a listagem é consultada de novo neste intervalo — é assim
// que o percentual e a etapa do backend chegam à tela, inclusive ao voltar para ela depois.
const INTERVALO_ATUALIZACAO_MS = 3000;

// Épico [AUDITORIA] Central de Entrada de Dados — página principal (DEVN3-643 + DEVN3-662).
//
// Fluxo (seção 50 do Épico "Central de Entrada de Dados"):
// Empresa → Período → Tipo Documental → Conta Contábil → Upload → Confirmação → Importação →
// Processamento → Resultado.
const AuditoriaEntradaDadosPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();

  // ── Contexto obrigatório (seção 8 do Épico) ────────────────────────────────────────────
  const [empresas, setEmpresas] = useState<IEmpresaAuditoria[]>([]);
  const [periodos, setPeriodos] = useState<IPeriodoAuditoria[]>([]);
  const [empresaId, setEmpresaId] = useState<number | undefined>(location.state?.empresaId);
  const [periodo, setPeriodo] = useState<string | undefined>(location.state?.periodo);

  const [tiposEntrada, setTiposEntrada] = useState<IAuditInputType[]>([]);
  const [tipoSelecionado, setTipoSelecionado] = useState<IAuditInputType | undefined>();

  const [contas, setContas] = useState<IContaContabilBancaria[]>([]);
  const [contaId, setContaId] = useState<number | undefined>();

  // ── Upload / confirmação ───────────────────────────────────────────────────────────────
  const [mostrarUploader, setMostrarUploader] = useState(false);
  const [arquivosSelecionados, setArquivosSelecionados] = useState<File[]>([]);
  const [mostrarConfirmacao, setMostrarConfirmacao] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [progressoEnvio, setProgressoEnvio] = useState(0);
  const [mensagemImportacao, setMensagemImportacao] = useState<
    { tipo: 'SUCCESS' | 'ERROR'; titulo: string; texto: string } | undefined
  >();

  // ── Listagem ────────────────────────────────────────────────────────────────────────────
  const [arquivos, setArquivos] = useState<IAuditFile[]>([]);
  const [carregandoArquivos, setCarregandoArquivos] = useState(false);
  const [idsReprocessando, setIdsReprocessando] = useState<Set<number>>(new Set());
  const [idsOcupados, setIdsOcupados] = useState<Set<number>>(new Set());

  // ── Modais de ação ──────────────────────────────────────────────────────────────────────
  const [arquivoParaExcluir, setArquivoParaExcluir] = useState<IAuditFile | undefined>();
  const [excluindo, setExcluindo] = useState(false);
  const [arquivoParaSubstituir, setArquivoParaSubstituir] = useState<IAuditFile | undefined>();
  const [substituindo, setSubstituindo] = useState(false);
  const [arquivoParaHistorico, setArquivoParaHistorico] = useState<IAuditFile | undefined>();
  const [arquivoParaResultado, setArquivoParaResultado] = useState<IAuditFile | undefined>();

  const [toast, setToast] = useState('');
  const mostrarToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  // `periodo` guarda o esteiraId (string) — mesmo formato usado por AuditoriaPage/AuditoriaFiltros
  // (ver <option value={String(item.esteiraId)}>) — para que o período já selecionado na tela de
  // auditoria (passado via location.state) seja reconhecido aqui e pré-preenchido corretamente.
  const esteiraId = periodo && Number(periodo) > 0 ? Number(periodo) : undefined;

  // ── Carregamento inicial ────────────────────────────────────────────────────────────────
  useEffect(() => {
    AuditoriaService.listarEmpresas().then(setEmpresas).catch(() => setEmpresas([]));
    AuditoriaEntradaDadosService.listarTiposEntrada()
      .then((tipos) => {
        setTiposEntrada(tipos);
        setTipoSelecionado(tipos.find((t) => t.code === 'BANK_STATEMENT'));
      })
      .catch(() => setTiposEntrada([]));
  }, []);

  useEffect(() => {
    if (!empresaId) {
      setPeriodos([]);
      return;
    }
    AuditoriaService.listarPeriodos(empresaId).then(setPeriodos).catch(() => setPeriodos([]));
  }, [empresaId]);

  // As contas de banco/caixa são as do período selecionado (cada período tem as suas).
  useEffect(() => {
    if (!esteiraId) {
      setContas([]);
      return;
    }
    AuditoriaEntradaDadosService.listarContasBancarias(esteiraId).then(setContas).catch(() => setContas([]));
  }, [esteiraId]);

  const carregarArquivos = useCallback(
    (silencioso: boolean = false) => {
      if (!esteiraId) {
        setArquivos([]);
        return;
      }
      if (!silencioso) setCarregandoArquivos(true);
      AuditoriaEntradaDadosService.listarArquivos(esteiraId)
        .then(setArquivos)
        .catch(() => {
          if (!silencioso) setArquivos([]);
        })
        .finally(() => {
          if (!silencioso) setCarregandoArquivos(false);
        });
    },
    [esteiraId]
  );

  useEffect(() => {
    carregarArquivos();
  }, [carregarArquivos]);

  // Seção 25/34 do Épico de Mensagens: ao retornar à tela (ou enquanto há processamento em
  // andamento), o status salvo no backend é consultado novamente.
  const haEmAndamento = arquivos.some((a) => arquivoEmAndamento(a.status));
  useEffect(() => {
    if (!haEmAndamento) return undefined;
    const timer = setInterval(() => carregarArquivos(true), INTERVALO_ATUALIZACAO_MS);
    return () => clearInterval(timer);
  }, [haEmAndamento, carregarArquivos]);

  const empresaSelecionada = empresas.find((e) => e.id === empresaId);
  const contaSelecionada = contas.find((c) => c.id === contaId);
  const periodoSelecionado = periodos.find((p) => String(p.esteiraId) === periodo);

  const periodoTexto = periodoSelecionado?.descricao ?? periodo ?? '';

  const contextoValido = !!empresaId && !!esteiraId && !!contaId && !!tipoSelecionado;

  // Seção 8/14 do Épico — mensagem individual por campo obrigatório (uma só por vez, na ordem
  // Empresa → Período → Conta Contábil → Arquivo; Empresa já é tratada por tela inteira acima).
  // O Período vem antes da Conta porque as contas de banco/caixa são as do período escolhido.
  const mensagemCampoObrigatorio = (): string | undefined => {
    if (!esteiraId) return 'Informe o período correspondente ao extrato.';
    if (!contaId) return 'Selecione uma Conta Contábil para continuar.';
    if (arquivosSelecionados.length === 0 && mostrarUploader) return 'Selecione um arquivo para continuar.';
    return undefined;
  };

  const uploaderRef = useRef<HTMLDivElement>(null);
  const abrirUploader = () => {
    setMensagemImportacao(undefined);
    setMostrarUploader(true);
  };

  // O botão "Adicionar Dados" do topo fica sempre visível sem rolar a página — ao clicar, rola
  // até o painel de upload para o usuário não precisar procurá-lo mais abaixo.
  useEffect(() => {
    if (mostrarUploader) {
      uploaderRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [mostrarUploader]);

  const marcarOcupado = (id: number, ocupado: boolean) =>
    setIdsOcupados((prev) => {
      const novo = new Set(prev);
      if (ocupado) novo.add(id);
      else novo.delete(id);
      return novo;
    });

  const confirmarImportacao = async () => {
    if (!tipoSelecionado || !esteiraId || !contaSelecionada) return;
    setEnviando(true);
    setProgressoEnvio(0);

    const contexto: IImportacaoContexto = {
      esteiraId,
      cartaoId: contaSelecionada.id,
      auditInputTypeCode: tipoSelecionado.code,
    };

    try {
      for (const file of arquivosSelecionados) {
        // eslint-disable-next-line no-await-in-loop
        await AuditoriaEntradaDadosService.importarArquivo(contexto, file, setProgressoEnvio);
      }
      setMensagemImportacao({
        tipo: 'SUCCESS',
        titulo: 'Importação concluída com sucesso',
        texto: 'O arquivo foi salvo e o processamento da conciliação será iniciado.',
      });
      setMostrarUploader(false);
      setMostrarConfirmacao(false);
      setArquivosSelecionados([]);
    } catch (err: any) {
      setMensagemImportacao({
        tipo: 'ERROR',
        titulo: 'Não foi possível concluir o envio do arquivo',
        texto: err?.response?.data?.message || 'Tente novamente.',
      });
      setMostrarConfirmacao(false);
    } finally {
      setEnviando(false);
      carregarArquivos(true);
    }
  };

  const handleReprocessar = async (arquivo: IAuditFile) => {
    setIdsReprocessando((prev) => new Set(prev).add(arquivo.id));
    const resultado = await AuditoriaEntradaDadosService.reprocessar(arquivo.id);
    setIdsReprocessando((prev) => {
      const novo = new Set(prev);
      novo.delete(arquivo.id);
      return novo;
    });
    mostrarToast(resultado.mensagem);
    carregarArquivos(true);
  };

  const handleExcluir = async () => {
    if (!arquivoParaExcluir) return;
    setExcluindo(true);
    const resultado = await AuditoriaEntradaDadosService.excluirArquivo(arquivoParaExcluir.id);
    setExcluindo(false);
    setArquivoParaExcluir(undefined);
    mostrarToast(resultado.sucesso ? 'Arquivo excluído com sucesso.' : resultado.mensagem);
    carregarArquivos(true);
  };

  const handleSubstituir = async (novoArquivo: File) => {
    if (!arquivoParaSubstituir) return;
    setSubstituindo(true);
    const resultado = await AuditoriaEntradaDadosService.substituirArquivo(arquivoParaSubstituir.id, novoArquivo);
    setSubstituindo(false);
    setArquivoParaSubstituir(undefined);
    mostrarToast(resultado.mensagem);
    carregarArquivos(true);
  };

  // PDF — depois da previsão de custo: processar (a leitura por IA começa) ou descartar.
  const handleDecidirPdf = async (arquivo: IAuditFile, processar: boolean) => {
    if (!processar) {
      const confirm = await Swal.fire({
        title: 'Descartar este PDF?',
        text: 'O arquivo não será lido pela IA e nenhum custo será gerado.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Descartar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d9534f',
      });
      if (!confirm.isConfirmed) return;
    }

    marcarOcupado(arquivo.id, true);
    const resultado = await AuditoriaEntradaDadosService.decidirPdf(arquivo.id, processar);
    marcarOcupado(arquivo.id, false);
    mostrarToast(resultado.mensagem);
    carregarArquivos(true);
  };

  // PDF — leitura que não fechou com os saldos: importar mesmo assim, reler ou descartar.
  const handleConfirmarPdf = async (arquivo: IAuditFile, acao: AcaoConfirmarPdf) => {
    const conferencia = arquivo.pdf?.conferencia;
    const textos = {
      IMPORTAR: {
        title: 'Importar mesmo assim?',
        text:
          conferencia?.tipo === 'SEM_SALDO'
            ? `Serão considerados ${conferencia.quantidade} lançamento(s) sem conferência de saldo. Confira a quantidade e os totais com o PDF antes de confirmar.`
            : `Serão considerados ${conferencia?.quantidade ?? 0} lançamento(s) mesmo com a diferença de saldo apontada.`,
        confirmButtonText: 'Importar',
      },
      RELER: {
        title: 'Reler as páginas divergentes?',
        text: 'A IA lê de novo apenas as páginas em que o saldo não fechou. Essa releitura gera um novo custo, proporcional a essas páginas.',
        confirmButtonText: 'Reler',
      },
      DESCARTAR: {
        title: 'Descartar este PDF?',
        text: 'A leitura feita será desconsiderada e nenhum lançamento deste arquivo entrará na auditoria.',
        confirmButtonText: 'Descartar',
      },
    }[acao];

    const confirm = await Swal.fire({
      ...textos,
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: acao === 'DESCARTAR' ? '#d9534f' : undefined,
    });
    if (!confirm.isConfirmed) return;

    marcarOcupado(arquivo.id, true);
    const resultado = await AuditoriaEntradaDadosService.confirmarPdf(arquivo.id, acao);
    marcarOcupado(arquivo.id, false);
    mostrarToast(resultado.mensagem);
    carregarArquivos(true);
  };

  return (
    <div className='auditoria-page audit-entrada-dados-page'>
      <div className='auditoria-header-card'>
        <div className='d-flex justify-content-between align-items-start mb-5'>
          <div>
            <button
              type='button'
              className='btn btn-sm btn-light mb-3'
              onClick={() => history.push('/auditoria')}
            >
              <i className='bi bi-arrow-left me-1' /> Voltar para Auditoria
            </button>
            <h2 className='auditoria-title'>Entrada de Dados</h2>
            <p className='auditoria-subtitle'>
              Auditoria → Entrada de Dados → Dados Bancários → Extrato Bancário
            </p>
          </div>
          {empresaId && (
            <button type='button' className='btn btn-primary' onClick={abrirUploader}>
              <i className='bi bi-cloud-arrow-up me-1' /> Adicionar Dados
            </button>
          )}
        </div>

        <div className='row g-3'>
          <div className='col-md-4'>
            <label className='form-label fw-bold fs-8'>Empresa</label>
            <Select<IOption<number>>
              inputId='entrada-dados-select-empresa'
              classNamePrefix='audit-select'
              placeholder='Selecione a empresa...'
              options={empresas.map((e) => ({ value: e.id, label: e.nomeFantasia || e.razaoSocial || e.cnpj }))}
              value={
                empresaId
                  ? { value: empresaId, label: empresaSelecionada?.nomeFantasia || empresaSelecionada?.razaoSocial || '' }
                  : null
              }
              onChange={(opt: SingleValue<IOption<number>>) => {
                setEmpresaId(opt?.value);
                setPeriodo(undefined);
                setContaId(undefined);
              }}
            />
          </div>
          <div className='col-md-4'>
            <label className='form-label fw-bold fs-8'>Período</label>
            <Select<IOption<string>>
              inputId='entrada-dados-select-periodo'
              classNamePrefix='audit-select'
              placeholder='Selecione o período...'
              isDisabled={!empresaId}
              options={periodos.map((p) => ({ value: String(p.esteiraId), label: p.descricao }))}
              value={periodo ? { value: periodo, label: periodoTexto } : null}
              onChange={(opt: SingleValue<IOption<string>>) => {
                setPeriodo(opt?.value);
                setContaId(undefined);
              }}
            />
          </div>
          <div className='col-md-4'>
            <label className='form-label fw-bold fs-8'>Conta Contábil do Banco</label>
            <Select<IOption<number>>
              inputId='entrada-dados-select-conta'
              classNamePrefix='audit-select'
              placeholder={esteiraId ? 'Selecione a conta...' : 'Selecione o período primeiro...'}
              noOptionsMessage={() => 'Nenhuma conta de banco ou caixa neste período.'}
              isDisabled={!esteiraId}
              options={contas.map((c) => ({ value: c.id, label: `${c.codigo} – ${c.descricao}` }))}
              value={
                contaId
                  ? { value: contaId, label: `${contaSelecionada?.codigo} – ${contaSelecionada?.descricao}` }
                  : null
              }
              onChange={(opt: SingleValue<IOption<number>>) => setContaId(opt?.value)}
            />
          </div>
        </div>

        {!empresaId && (
          <div className='mt-4'>
            <AuditMessage type='INFO' title='Selecione uma Empresa para continuar.' />
          </div>
        )}
      </div>

      {empresaId && (
        <>
          {/* Categoria → Tipo Documental (seção 6/7 do Épico) */}
          <div className='audit-card mb-4'>
            <div className='fw-bold fs-6 mb-3'>Categoria: Dados Bancários</div>
            <div className='audit-tipo-grid'>
              {tiposEntrada.map((tipo) => (
                <button
                  key={tipo.code}
                  type='button'
                  disabled={!tipo.enabled}
                  className={`audit-tipo-card ${tipoSelecionado?.code === tipo.code ? 'audit-tipo-card-ativo' : ''}`}
                  onClick={() => tipo.enabled && setTipoSelecionado(tipo)}
                >
                  <i className={`bi ${tipo.code === 'BANK_STATEMENT' ? 'bi-bank' : 'bi-file-earmark-text'} audit-tipo-card-icon`} />
                  <div className='audit-tipo-card-nome'>{tipo.name}</div>
                  {!tipo.enabled && <span className='badge badge-light-secondary mt-1'>Em breve</span>}
                </button>
              ))}
            </div>
          </div>

          {mensagemImportacao && (
            <div className='mb-4'>
              <AuditMessage
                type={mensagemImportacao.tipo}
                title={mensagemImportacao.titulo}
                message={mensagemImportacao.texto}
                closable
                onClose={() => setMensagemImportacao(undefined)}
              />
            </div>
          )}

          <div className='audit-card mb-4'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div className='fw-bold fs-6'>Arquivos — {tipoSelecionado?.name ?? 'Extrato Bancário'}</div>
              <button type='button' className='btn btn-sm btn-primary' onClick={abrirUploader}>
                <i className='bi bi-plus-lg me-1' /> Adicionar Dados
              </button>
            </div>

            {mostrarUploader && (
              <div className='audit-uploader-wrap mb-4' ref={uploaderRef}>
                {mensagemCampoObrigatorio() && (
                  <div className='mb-3'>
                    <AuditMessage type='ERROR' title={mensagemCampoObrigatorio()!} />
                  </div>
                )}
                <AuditFileUploader
                  acceptedExtensions={tipoSelecionado?.acceptedExtensions ?? ['pdf', 'ofx']}
                  multiple={!!tipoSelecionado?.multipleFiles}
                  disabled={!contextoValido}
                  onFilesSelected={setArquivosSelecionados}
                />
                <div className='d-flex justify-content-end gap-2 mt-3'>
                  <button type='button' className='btn btn-sm btn-light' onClick={() => { setMostrarUploader(false); setArquivosSelecionados([]); }}>
                    Cancelar
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-primary'
                    disabled={arquivosSelecionados.length === 0 || !contextoValido}
                    onClick={() => setMostrarConfirmacao(true)}
                  >
                    Revisar e Importar
                  </button>
                </div>
              </div>
            )}

            {/* Enquanto o upload está aberto e ainda não há nenhum arquivo importado, esconder a
                listagem evita o estado vazio ("Nenhum arquivo importado" + um 2º botão "Adicionar
                Dados") logo abaixo do painel de upload já aberto — eram duas seções sobre o mesmo
                assunto na tela ao mesmo tempo, dando a impressão de que o arquivo selecionado não
                tinha sido reconhecido. */}
            {(!mostrarUploader || arquivos.length > 0) && (
              <AuditFileList
                arquivos={arquivos}
                carregando={carregandoArquivos}
                onAdicionarDados={abrirUploader}
                onVerHistorico={setArquivoParaHistorico}
                onSubstituir={setArquivoParaSubstituir}
                onReprocessar={handleReprocessar}
                onExcluir={setArquivoParaExcluir}
                onVerResultado={setArquivoParaResultado}
                onDecidirPdf={handleDecidirPdf}
                onConfirmarPdf={handleConfirmarPdf}
                idsReprocessando={idsReprocessando}
                idsOcupados={idsOcupados}
              />
            )}
          </div>
        </>
      )}

      {mostrarConfirmacao && tipoSelecionado && empresaSelecionada && contaSelecionada && (
        <AuditImportConfirmation
          empresaNome={empresaSelecionada.nomeFantasia || empresaSelecionada.razaoSocial || ''}
          contaLabel={`${contaSelecionada.codigo} – ${contaSelecionada.descricao}`}
          periodoTexto={periodoTexto}
          tipoDocumental={tipoSelecionado.name}
          arquivos={arquivosSelecionados}
          enviando={enviando}
          progresso={progressoEnvio}
          onCancelar={() => setMostrarConfirmacao(false)}
          onConfirmar={confirmarImportacao}
        />
      )}

      {arquivoParaExcluir && (
        <AuditDeleteFileModal
          nomeArquivo={arquivoParaExcluir.originalFileName}
          excluindo={excluindo}
          onCancelar={() => setArquivoParaExcluir(undefined)}
          onConfirmar={handleExcluir}
        />
      )}

      {arquivoParaSubstituir && (
        <AuditReplaceFileModal
          nomeArquivoAtual={arquivoParaSubstituir.originalFileName}
          acceptedExtensions={tipoSelecionado?.acceptedExtensions ?? ['pdf', 'ofx']}
          substituindo={substituindo}
          onCancelar={() => setArquivoParaSubstituir(undefined)}
          onConfirmar={handleSubstituir}
        />
      )}

      {arquivoParaHistorico && (
        <AuditFileHistory
          auditFileId={arquivoParaHistorico.id}
          nomeArquivo={arquivoParaHistorico.originalFileName}
          onFechar={() => setArquivoParaHistorico(undefined)}
        />
      )}

      {arquivoParaResultado && arquivoParaResultado.accountingAccountId && (arquivoParaResultado.esteiraId ?? esteiraId) && (
        <AuditReconciliationResult
          esteiraId={(arquivoParaResultado.esteiraId ?? esteiraId)!}
          cartaoId={arquivoParaResultado.accountingAccountId}
          contaLabel={arquivoParaResultado.accountingAccountLabel ?? ''}
          onFechar={() => setArquivoParaResultado(undefined)}
        />
      )}

      {toast && <div className='audit-toast'>{toast}</div>}
    </div>
  );
};

export default AuditoriaEntradaDadosPage;
